import { registerSW } from 'virtual:pwa-register';

export interface PwaState {
  readonly registration: 'error' | 'idle' | 'registered' | 'registering';
  readonly offlineReady: boolean;
  readonly update: 'applying' | 'available' | 'failed' | 'idle';
}

type PwaStateListener = () => void;
type UpdateServiceWorker = ReturnType<typeof registerSW>;

const listeners = new Set<PwaStateListener>();
let state: PwaState = {
  registration: 'idle',
  offlineReady: false,
  update: 'idle',
};
let registrationStarted = false;
let updateServiceWorker: UpdateServiceWorker | null = null;

interface ControllerChangeObserver {
  readonly promise: Promise<void>;
  cancel(): void;
}

function publishPwaState(patch: Partial<PwaState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) {
    listener();
  }
}

export function getAppPwaState(): PwaState {
  return state;
}

export function subscribeAppPwaState(listener: PwaStateListener): () => void {
  listeners.add(listener);
  listener();
  return () => listeners.delete(listener);
}

function observeControllerReplacement(
  previousController: ServiceWorker | null,
): ControllerChangeObserver {
  let cleanup = () => undefined;
  const promise = new Promise<void>((resolveChange, rejectChange) => {
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      if (error === undefined) {
        resolveChange();
      } else {
        rejectChange(error);
      }
    };
    const onControllerChange = () => {
      const nextController = navigator.serviceWorker.controller;
      if (nextController !== null && nextController !== previousController) {
        finish();
      }
    };
    const timeout = window.setTimeout(
      () => finish(new Error('The service-worker update did not take control in time.')),
      15_000,
    );
    cleanup = () => {
      window.clearTimeout(timeout);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    onControllerChange();
  });
  return { promise, cancel: () => cleanup() };
}

async function waitForActivatedRegistration(): Promise<void> {
  const deadline = Date.now() + 15_000;
  while (Date.now() < deadline) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration !== undefined && registration.waiting === null) {
      if (registration.active?.state === 'activated') {
        return;
      }
    }
    await new Promise<void>((resolveDelay) => window.setTimeout(resolveDelay, 25));
  }
  throw new Error('The service-worker update did not reach the activated state in time.');
}

export function registerAppServiceWorker(): void {
  if (registrationStarted) {
    return;
  }
  registrationStarted = true;
  publishPwaState({ registration: 'registering' });
  updateServiceWorker = registerSW({
    immediate: true,
    onNeedReload() {
      // applyPendingAppUpdate verifies controller replacement before reloading.
    },
    onNeedRefresh() {
      publishPwaState({ update: 'available' });
    },
    onOfflineReady() {
      publishPwaState({ offlineReady: true });
    },
    onRegisteredSW() {
      publishPwaState({ registration: 'registered' });
      void navigator.serviceWorker.ready.then(
        () => publishPwaState({ offlineReady: true }),
        () => publishPwaState({ registration: 'error' }),
      );
    },
    onRegisterError() {
      publishPwaState({ registration: 'error' });
    },
  });
}

export async function applyPendingAppUpdate(beforeActivation: () => Promise<void>): Promise<void> {
  if ((state.update !== 'available' && state.update !== 'failed') || updateServiceWorker === null) {
    throw new Error('No confirmed service-worker update is waiting for activation.');
  }
  publishPwaState({ update: 'applying' });
  let controllerChange: ControllerChangeObserver | null = null;
  try {
    await beforeActivation();
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration?.waiting === null || registration?.waiting === undefined) {
      throw new Error('The confirmed service-worker update is no longer waiting.');
    }
    controllerChange = observeControllerReplacement(navigator.serviceWorker.controller);
    await updateServiceWorker(true);
    await controllerChange.promise;
    await waitForActivatedRegistration();
    publishPwaState({ update: 'idle' });
    window.location.reload();
  } catch (error) {
    controllerChange?.cancel();
    publishPwaState({ update: 'failed' });
    console.error('Service-worker update activation failed.', error);
    throw error;
  }
}
